import { composeContactSheet, contactSheetLayout } from './contactSheet';
import { canvasToBlob } from './encoding';
import { ANIMATION_RENDER_LIMITS } from './limits';
import { encodeFramesWithMediaRecorder, type EncodeFramesInput } from './mediaRecorderEncoder';
import type { VideoRange } from './timeline';
import {
  throwIfAborted,
  UnsupportedMediaError,
  type AnimationRenderScene,
  type CreateCanvas,
  type EncodedArtifact,
  type EncodedVideo,
  type FrameRenderer,
  type FrameSession,
} from './types';
import type { VideoFormat } from './videoFormat';

type PipelineOptions = {
  signal?: AbortSignal;
};

async function withSession<T>(
  renderer: FrameRenderer,
  scene: AnimationRenderScene,
  signal: AbortSignal | undefined,
  work: (session: FrameSession) => Promise<T>,
): Promise<T> {
  throwIfAborted(signal);
  const session = await renderer.open(scene, signal);
  try {
    throwIfAborted(signal);
    return await work(session);
  } finally {
    await session.close();
  }
}

/** Stage 4: frames in timestamp order. Each frame is handed to `keep` before the next renders. */
export async function renderFrameSequence<T>(
  session: FrameSession,
  timestamps: readonly number[],
  keep: (frame: HTMLCanvasElement, timeSeconds: number) => T,
  signal?: AbortSignal,
): Promise<T[]> {
  const kept: T[] = [];
  for (const timeSeconds of timestamps) {
    throwIfAborted(signal);
    const frame = await session.renderFrame(timeSeconds, signal);
    kept.push(keep(frame, timeSeconds));
  }
  throwIfAborted(signal);
  return kept;
}

export async function renderFramePng(
  renderer: FrameRenderer,
  scene: AnimationRenderScene,
  timeSeconds: number,
  { signal }: PipelineOptions = {},
): Promise<EncodedArtifact & { timeSeconds: number }> {
  return withSession(renderer, scene, signal, async (session) => {
    const frame = await session.renderFrame(timeSeconds, signal);
    throwIfAborted(signal);
    const blob = await canvasToBlob(frame, 'image/png');
    return {
      blob,
      mimeType: 'image/png',
      width: frame.width,
      height: frame.height,
      timeSeconds,
      warnings: [...session.warnings],
    };
  });
}

export async function renderContactSheetPng(
  renderer: FrameRenderer,
  scene: AnimationRenderScene,
  timestamps: readonly number[],
  { signal, createCanvas }: PipelineOptions & { createCanvas: CreateCanvas },
): Promise<EncodedArtifact & { timestamps: number[]; frameWidth: number; frameHeight: number }> {
  return withSession(renderer, scene, signal, async (session) => {
    const layout = contactSheetLayout(
      timestamps.length,
      session.size,
      ANIMATION_RENDER_LIMITS.maxContactSheetEdgePx,
    );
    const frames = await renderFrameSequence(
      session,
      timestamps,
      (frame, timeSeconds) => ({
        image: scaledCopy(frame, layout.cellWidth, layout.cellHeight, createCanvas),
        timeSeconds,
      }),
      signal,
    );
    const sheet = composeContactSheet(frames, layout, createCanvas);
    frames.forEach((frame) => releaseCanvas(frame.image));
    const blob = await canvasToBlob(sheet, 'image/png');
    releaseCanvas(sheet);
    const warnings = [...session.warnings];
    if (layout.scale < 1) {
      warnings.push(
        `Frames were scaled to ${Math.round(layout.scale * 100)}% to fit a ${
          ANIMATION_RENDER_LIMITS.maxContactSheetEdgePx
        }px contact sheet.`,
      );
    }
    return {
      blob,
      mimeType: 'image/png',
      width: layout.width,
      height: layout.height,
      frameWidth: layout.cellWidth,
      frameHeight: layout.cellHeight,
      timestamps: [...timestamps],
      warnings,
    };
  });
}

/** H.264 needs even dimensions. */
function evenFloor(value: number): number {
  return Math.max(2, Math.floor(value / 2) * 2);
}

export function videoFrameScale(
  frameCount: number,
  size: { width: number; height: number },
  requestedScale = 1,
): number {
  const budget = ANIMATION_RENDER_LIMITS.maxVideoFramePixels;
  const pixels = frameCount * size.width * size.height * requestedScale * requestedScale;
  if (pixels <= budget) {
    return requestedScale;
  }
  return requestedScale * Math.sqrt(budget / pixels);
}

export type RenderReviewVideoOptions = PipelineOptions & {
  createCanvas: CreateCanvas;
  format: VideoFormat | null;
  scale?: number;
  MediaRecorderImpl?: EncodeFramesInput['MediaRecorderImpl'];
};

export async function renderReviewVideo(
  renderer: FrameRenderer,
  scene: AnimationRenderScene,
  range: VideoRange,
  { signal, createCanvas, format, scale = 1, MediaRecorderImpl }: RenderReviewVideoOptions,
): Promise<EncodedVideo & { startSeconds: number; endSeconds: number }> {
  if (!format) {
    throw new UnsupportedMediaError('This browser cannot record MP4 or WebM.');
  }
  return withSession(renderer, scene, signal, async (session) => {
    const warnings = [...session.warnings];
    const resolvedScale = videoFrameScale(range.frameTimes.length, session.size, scale);
    if (resolvedScale < scale) {
      warnings.push(
        `Video frames were scaled to ${Math.round(
          resolvedScale * 100,
        )}% to stay within the frame memory budget.`,
      );
    }
    const width = evenFloor(session.size.width * resolvedScale);
    const height = evenFloor(session.size.height * resolvedScale);
    const frames = await renderFrameSequence(
      session,
      range.frameTimes,
      (frame) => scaledCopy(frame, width, height, createCanvas),
      signal,
    );
    try {
      const { blob, format: recorded } = await encodeFramesWithMediaRecorder({
        frames,
        width,
        height,
        fps: range.fps,
        format,
        createCanvas,
        signal,
        MediaRecorderImpl,
      });
      return {
        blob,
        mimeType: recorded.mimeType,
        container: recorded.container,
        codec: recorded.codec,
        width,
        height,
        fps: range.fps,
        durationSeconds: range.durationSeconds,
        startSeconds: range.startSeconds,
        endSeconds: range.endSeconds,
        warnings,
      };
    } finally {
      frames.forEach(releaseCanvas);
    }
  });
}

function scaledCopy(
  frame: HTMLCanvasElement,
  width: number,
  height: number,
  createCanvas: CreateCanvas,
): HTMLCanvasElement {
  if (frame.width === width && frame.height === height) {
    return frame;
  }
  const copy = createCanvas(width, height);
  const context = copy.getContext('2d');
  if (!context) {
    throw new Error('A 2D canvas is not available.');
  }
  context.drawImage(frame, 0, 0, width, height);
  releaseCanvas(frame);
  return copy;
}

function releaseCanvas(canvas: CanvasImageSource): void {
  if (typeof HTMLCanvasElement !== 'undefined' && canvas instanceof HTMLCanvasElement) {
    canvas.width = 0;
    canvas.height = 0;
  }
}

export function createDomCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}
